import { z } from 'zod';
import { ErrorCode } from '../../errors/codes.js';
import { ArtifactSchema } from './artifact.js';
import { AgentVerdictSchema } from './verdict.js';

// ---- SSE event variants -----------------------------------------------------
//
// Every server-sent event from `POST /api/dashboards/agent/ask` is one of these
// shapes. The client must switch on `type` and handle each branch; unknown
// types are rejected so a stale client fails loudly rather than silently
// dropping events. (v2 streams a distinct union from
// `POST /api/dashboards/agent/v2/ask` — see `AgentV2EventSchema`.)

const StepEventSchema = z
  .object({
    type: z.literal('step'),
    label: z.string(),
    status: z.enum(['start', 'done']),
  })
  .strict();

const TokenEventSchema = z
  .object({
    type: z.literal('token'),
    text: z.string(),
  })
  .strict();

const ArtifactEventSchema = z
  .object({
    type: z.literal('artifact'),
    artifact: ArtifactSchema,
  })
  .strict();

const VerdictEventSchema = z
  .object({
    type: z.literal('verdict'),
    verdict: AgentVerdictSchema,
  })
  .strict();

const DoneEventSchema = z
  .object({
    type: z.literal('done'),
    conversationId: z.string().uuid(),
  })
  .strict();

// Coarse failure phase of an agent run, attached to the terminal `error` event
// so an operator can tell WHERE the run failed (not just the ErrorCode), for
// distinct safe web copy + `{stage, code}` logging (#2005).
export const AGENT_ERROR_STAGES = [
  'auth',
  'stream',
  'decompose',
  'compute',
  'compare',
  'compose',
  'sql',
  'model',
  'zod',
  'timeout',
  'abort',
  'unknown',
] as const;
export type AgentErrorStage = (typeof AGENT_ERROR_STAGES)[number];

// WHY: uses z.nativeEnum(ErrorCode) — the error event's `code` field must
//   be one of the domain ErrorCode values, not an arbitrary string, so the
//   client can branch on a typed code rather than fragile message matching.
// WHY: `stage` is OPTIONAL for live-client back-compat — a deployed v1 client
//   (and any pre-#2005 emitted event) has no `stage` and must still parse; a
//   required field here would desync the already-shipped client. The backend
//   always emits a concrete stage, so optionality is a parse-side concession.
const ErrorEventSchema = z
  .object({
    type: z.literal('error'),
    code: z.nativeEnum(ErrorCode),
    message: z.string(),
    stage: z.enum(AGENT_ERROR_STAGES).optional(),
  })
  .strict();

/**
 * @aiPurpose discriminated union of all SSE event payloads streamed by the
 *   agent run endpoint. Consumers MUST switch on `type` and handle every
 *   variant. The `done` event is always the last event in a successful run;
 *   `error` is terminal and indicates the run did not complete.
 * @aiInvariant `token` events accumulate into the assistant's prose answer.
 *   `artifact` events carry rich data cards. `verdict` carries the
 *   reconciliation result. `step` events drive the progress indicator.
 *   New event types require a coordinated shared + backend + web change.
 */
export const AgentEventSchema = z.discriminatedUnion('type', [
  StepEventSchema,
  TokenEventSchema,
  ArtifactEventSchema,
  VerdictEventSchema,
  DoneEventSchema,
  ErrorEventSchema,
]);

export type AgentEvent = z.infer<typeof AgentEventSchema>;
